import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M } from '@/business';

export const moveSchema = z.object({
  userId: z.string({ error: M.required }).min(1, M.required),
  teamId: z.string({ error: M.required }).min(1, M.required),
  validFrom: z.string({ error: M.required }).min(1, M.required),
});
export type MoveValues = z.infer<typeof moveSchema>;

export const PARTY_KINDS = ['bank_processor', 'bank_department', 'bank_rm', 'agency', 'other'] as const satisfies readonly Schema<'ExternalPartyKind'>[];
export const PARTY_ROLES = ['processor', 'verification_contact', 'offer_source', 'contact'] as const;
type Role = (typeof PARTY_ROLES)[number];

export const partySchema = z
  .object({
    name: z.string({ error: M.required }).trim().min(2, M.required),
    kind: z.enum(PARTY_KINDS),
    bankId: z.string().optional(),
    roles: z.object({ processor: z.boolean(), verification_contact: z.boolean(), offer_source: z.boolean(), contact: z.boolean() }),
    note: z.string().optional(),
  })
  .refine((v) => PARTY_ROLES.some((r) => v.roles[r]), { path: ['roles', 'processor'], message: M.required })
  .refine((v) => v.kind === 'agency' || v.kind === 'other' || Boolean(v.bankId), { path: ['bankId'], message: M.required });
export type PartyValues = z.infer<typeof partySchema>;

export const partyDefaults: PartyValues = { name: '', kind: 'bank_processor', bankId: '', roles: { processor: true, verification_contact: false, offer_source: false, contact: false }, note: '' };

export const toPartyInput = (v: PartyValues): Schema<'ExternalPartyInput'> => ({
  name: v.name.trim(),
  kind: v.kind,
  bankId: v.kind === 'agency' || v.kind === 'other' ? null : v.bankId || null,
  roles: PARTY_ROLES.filter((r: Role) => v.roles[r]),
  ...(v.note?.trim() ? { note: v.note.trim() } : {}),
});

/** Edit an existing party: the same fields, prefilled. */
export const toPartyValues = (p: Schema<'ExternalParty'>): PartyValues => ({
  name: p.name,
  kind: p.kind,
  bankId: p.bankId ?? '',
  roles: { processor: p.roles.includes('processor'), verification_contact: p.roles.includes('verification_contact'), offer_source: p.roles.includes('offer_source'), contact: p.roles.includes('contact') },
  note: p.note ?? '',
});

export const PRODUCT_CODES = ['PF', 'CC', 'HF', 'SME'] as const satisfies readonly Schema<'ProductCode'>[];

export const unitSchema = z
  .object({
    name: z.string({ error: M.required }).trim().min(2, M.required),
    headId: z.string(),
    products: z.object({ PF: z.boolean(), CC: z.boolean(), HF: z.boolean(), SME: z.boolean() }),
    showWorkingCases: z.boolean(),
  })
  .refine((v) => PRODUCT_CODES.some((p) => v.products[p]), { path: ['products', 'PF'], message: M.required });
export type UnitValues = z.infer<typeof unitSchema>;

export const toUnitValues = (u?: Schema<'BusinessUnit'>): UnitValues => ({
  name: u?.name ?? '',
  headId: u?.head?.id ?? '',
  products: { PF: Boolean(u?.products.includes('PF')), CC: Boolean(u?.products.includes('CC')), HF: Boolean(u?.products.includes('HF')), SME: Boolean(u?.products.includes('SME')) },
  showWorkingCases: u?.showWorkingCases ?? false,
});
export const toUnitInput = (v: UnitValues): Schema<'BusinessUnitInput'> => ({ name: v.name.trim(), headId: v.headId || null, products: PRODUCT_CODES.filter((p) => v.products[p]), showWorkingCases: v.showWorkingCases });

export const teamSchema = z.object({
  name: z.string({ error: M.required }).trim().min(2, M.required),
  unitId: z.string({ error: M.required }).min(1, M.required),
  branch: z.string({ error: M.required }).min(1, M.required),
  managerId: z.string(),
  leaderId: z.string({ error: M.required }).min(1, M.required),
});
export type TeamValues = z.infer<typeof teamSchema>;
export const toTeamValues = (t?: Schema<'TeamDetail'>): TeamValues => ({ name: t?.name ?? '', unitId: t?.unit?.id ?? '', branch: t?.branch ?? 'Dubai', managerId: t?.manager?.id ?? '', leaderId: t?.leader.id ?? '' });
export const toTeamInput = (v: TeamValues): Schema<'TeamInput'> => ({ name: v.name.trim(), unitId: v.unitId, branch: v.branch, managerId: v.managerId || null, leaderId: v.leaderId });

/** `needsTarget`: the team still has open deals or members, so a team must take them over. */
export const closeTeamSchema = (needsTarget: boolean) =>
  z.object({
    effectiveDate: z.string({ error: M.required }).regex(/^\d{4}-\d{2}-\d{2}$/, M.date),
    reassignToTeamId: needsTarget ? z.string({ error: M.required }).min(1, M.required) : z.string(),
  });
export type CloseTeamValues = z.infer<ReturnType<typeof closeTeamSchema>>;
